import { computed, ref } from 'vue';
import { getCompiledSchema } from '@/validations/ajv';
import { i18n } from '@/core/i18n';
import { omitEmptyOptionalSchemaProperties } from '@/shared/utilities/payload-utils';
import { getActualAjvError, translateAjvError } from '@/validations/shared/validation-errors';

const errorPriority = (error) => {
  if (['required', 'minLength', 'maxLength'].includes(getActualAjvError(error).keyword)) {
    return 0;
  }
  return 1;
};

const toFieldPath = (path = '') => path.replace(/\/(\d+)/g, '[$1]').replaceAll('/', '.');

const getErrorFieldPath = (error) => {
  let path = error.instancePath.slice(1);
  const source = getActualAjvError(error);
  // A grouped custom message belongs to the group, not its first failing child.
  if (error.keyword === 'errorMessage' && error.params.errors.length > 1) {
    return toFieldPath(path);
  }
  if (source.keyword === 'required') {
    path = [source.instancePath.slice(1), source.params.missingProperty].filter(Boolean).join('/');
  }
  return toFieldPath(path);
};

export function useValidate() {
  const errors = ref({});
  const hasErrors = computed(() => Object.keys(errors.value).length > 0);

  const validateData = (schema, data) => {
    errors.value = {};
    const validateSchema = getCompiledSchema(schema);
    const normalizedData = omitEmptyOptionalSchemaProperties(data, schema);
    const isValid = validateSchema(normalizedData);
    const validationErrors = [...(validateSchema.errors ?? [])]
      .filter((error) => error.keyword !== 'if')
      .sort((a, b) => errorPriority(a) - errorPriority(b));

    for (const error of validationErrors) {
      const field = getErrorFieldPath(error);
      if (!errors.value[field]) {
        errors.value[field] = translateAjvError(error, i18n.global.t);
      }
    }

    return { isValid, data: normalizedData };
  };

  const validate = (schema, data) => validateData(schema, data).isValid;

  const clearErrors = (...fields) => {
    if (!fields.length) {
      errors.value = {};
      return;
    }
    for (const path of Object.keys(errors.value)) {
      if (
        fields.some(
          (field) => path === field || path.startsWith(`${field}.`) || path.startsWith(`${field}[`),
        )
      ) {
        delete errors.value[path];
      }
    }
  };

  return { clearErrors, errors, hasErrors, validate, validateData };
}
